import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Button,
  Badge,
  Tooltip,
  OverlayTrigger,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import { MdOutlineSort } from "react-icons/md";
import {
  FiInfo,
  FiX,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiBox,
} from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

// 1. LIBRERIE PER LE DATE E LE FESTIVITÀ
import { addDays, isSameDay, isWeekend } from "date-fns";
import Holidays from "date-holidays";

const hd = new Holidays("IT");

/**
 * Calcola i giorni feriali (lavorativi) effettivi passati tra due date,
 * escludendo i weekend e le festività nazionali italiane.
 */
const calcolaGiorniFeriali = (dataInizioStr) => {
  if (!dataInizioStr) return 0;

  const dataInizio = new Date(dataInizioStr.split(" ")[0]);
  const oggi = new Date();

  if (dataInizio >= oggi) return 0;

  let giorniFeriali = 0;
  let dataCorrente = dataInizio;

  while (!isSameDay(dataCorrente, oggi) && dataCorrente < oggi) {
    dataCorrente = addDays(dataCorrente, 1);
    const weekend = isWeekend(dataCorrente);
    const festivo = hd.isHoliday(dataCorrente);

    if (!weekend && !festivo) {
      giorniFeriali++;
    }
  }

  return giorniFeriali;
};

const TabellaRimessiCompleta = () => {
  const [rimessiRaggruppati, setRimessiRaggruppati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "Data Rimesso",
    direction: "asc",
  });
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [selectedRimesso, setSelectedRimesso] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        // 1. Mappa dei Target Spedizione da STATO_ORDINI
        const ordiniRaw = data?.STATO_ORDINI?.flat() || [];
        const mappaTargetFornitori = {};

        ordiniRaw.forEach((ordine) => {
          const nomeFornitore =
            ordine["Ragione sociale"] || ordine["Ragione Sociale"];
          const giorniSped = ordine["Giorni Spedizione"];
          if (nomeFornitore && giorniSped !== undefined) {
            mappaTargetFornitori[nomeFornitore.trim().toUpperCase()] =
              parseInt(giorniSped) || 0;
          }
        });

        // 2. Elaborazione e Raggruppamento Rimessi
        const datiRaw = data?.RIMESSI?.flat() || [];
        const raggruppati = datiRaw.reduce((acc, curr) => {
          const numDoc = curr["Num Rimesso"];
          const nomeFornitoreRimesso = (
            curr["Ragione Sociale"] || ""
          ).trim().toUpperCase();

          const targetSpedizione =
            mappaTargetFornitori[nomeFornitoreRimesso] !== undefined
              ? mappaTargetFornitori[nomeFornitoreRimesso]
              : 0;

          if (!acc[numDoc]) {
            acc[numDoc] = {
              ...curr,
              giorniSpedizioneTarget: targetSpedizione,
              dettaglioArticoli: [curr],
              totQta: parseFloat(curr.Qta || 0),
              totImporto: parseFloat(curr.Importo || 0),
              totSpuntata: parseFloat(curr["Qta Spuntata"] || 0),
            };
          } else {
            acc[numDoc].dettaglioArticoli.push(curr);
            acc[numDoc].totQta += parseFloat(curr.Qta || 0);
            acc[numDoc].totImporto += parseFloat(curr.Importo || 0);
            acc[numDoc].totSpuntata += parseFloat(curr["Qta Spuntata"] || 0);
          }
          return acc;
        }, {});

        const ordinati = Object.values(raggruppati).sort((a, b) => {
          return new Date(a["Data Rimesso"]) - new Date(b["Data Rimesso"]);
        });

        setRimessiRaggruppati(ordinati);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch:", err);
        setLoading(false);
      });
  }, []);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });

    const sortedData = [...rimessiRaggruppati].sort((a, b) => {
      let valA = a[key] ?? "";
      let valB = b[key] ?? "";

      if (key === "Data Rimesso") {
        valA = new Date(valA);
        valB = new Date(valB);
      } else if (
        key === "totImporto" ||
        key === "totQta" ||
        key === "totSpuntata" ||
        key === "giorniSpedizioneTarget"
      ) {
        valA = Number(valA);
        valB = Number(valB);
      }

      return direction === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
        ? 1
        : -1;
    });
    setRimessiRaggruppati(sortedData);
  };

  const renderTooltipSpiegazione = (props) => (
    <Tooltip
      id="info-rimessi-tooltip"
      {...props}
      style={{ ...props.style, fontSize: "0.85rem" }}
    >
      Questo pannello mostra la <strong>Gestione dei Rimessi</strong> (merce
      respinta o reinviata al fornitore). Clicca su una riga per aprirne il dettaglio.
    </Tooltip>
  );

  const isOlderThan5Days = (dataRimesso) => {
    const diffDaysFeriali = calcolaGiorniFeriali(dataRimesso);
    return diffDaysFeriali > 5;
  };

  return (
    <div
      className="card border-0 p-0"
      style={{
        borderRadius: "10px",
        height: "380px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header statico del Widget nella Dashboard */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div
          className="d-flex align-items-center"
          onClick={() => setShowFullscreenModal(true)}
          style={{ cursor: "pointer" }}
        >
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            RIMESSI DEP. 04 (SENZA BEM)
          </span>

          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
        <small className="text-muted fw-bold">
          {rimessiRaggruppati.length} DOCUMENTI
        </small>
      </div>

      <style>{`
          .scroll-area::-webkit-scrollbar { width: 4px; }
          .scroll-area::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }
          .table th { cursor: pointer; user-select: none; }
          .table th:hover { background-color: #f1f3f5 !important; }
          .table td { padding: 10px 8px !important; }
      `}</style>

      {/* Tabella Widget Sintetica */}
      <div
        className="scroll-area p-0"
        style={{
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        <Table className="table table-hover mb-0" style={{ fontSize: "0.70rem" }}>
          <thead
            className="bg-white sticky-top"
            style={{ zIndex: 1, boxShadow: "0 2px 2px -1px rgba(0,0,0,0.05)" }}
          >
            <tr>
              <th onClick={() => requestSort("Dep")} style={{ width: "60px" }}>
                Dep
              </th>
              <th onClick={() => requestSort("Ragione Sociale")}>
                Fornitore <MdOutlineSort />
              </th>
              <th
                onClick={() => requestSort("Data Rimesso")}
                style={{ width: "200px" }}
              >
                Data
              </th>
              <th
                onClick={() => requestSort("Num Rimesso")}
                style={{ width: "110px" }}
              >
                Num. Rimesso
              </th>
              <th
                onClick={() => requestSort("Importo")}
                style={{ width: "110px" }}
              >
                Importo
              </th>
              <th className="text-center" style={{ width: "70px" }}>
                Articoli
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  Caricamento in corso...
                </td>
              </tr>
            ) : rimessiRaggruppati.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  Nessun rimesso trovato
                </td>
              </tr>
            ) : (
              rimessiRaggruppati.map((row, idx) => (
                <tr
                  key={idx}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedRimesso(row);
                    setShowModal(true);
                  }}
                >
                  <td style={{ fontWeight: "700", verticalAlign: "middle" }}>
                    {row["Dep"]}
                  </td>
                  <td
                    style={{ fontWeight: "600", verticalAlign: "middle" }}
                    className="text-dark text-truncate"
                    title={row["Ragione Sociale"]}
                  >
                    {row["Ragione Sociale"]}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {row["Data Rimesso"]
                      ? new Date(row["Data Rimesso"]).toLocaleDateString("it-IT")
                      : "-"}
                  </td>
                  <td
                    style={{ fontWeight: "700", verticalAlign: "middle" }}
                    className="text-primary"
                  >
                    {row["Num Rimesso"]}
                  </td>
                  <td
                    style={{ fontWeight: "700", verticalAlign: "middle" }}
                    className="fw-bold"
                  >
                     € {Number(row["totImporto"]).toFixed(2)}
                  </td>
                  <td className="text-center" style={{ verticalAlign: "middle" }}>
                    <Badge bg="light" text="dark" className="border px-2 py-1">
                      {row.dettaglioArticoli.length}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* MODALE TUTTO SCHERMO CON TUTTE LE INFO DEL JSON IN TABELLA */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen={true}
        animation={true}
      >
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          {/* Header Modale Fullscreen */}
          <div
            className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm"
            style={{ zIndex: 110 }}
          >
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "1rem",
                  color: "#1a1a1a",
                  letterSpacing: "1px",
                  marginRight: "8px",
                }}
              >
                TABELLA COMPLETA RIMESSI DEP. 04
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span
                  style={{
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <FiInfo size={16} color="#adb5bd" />
                </span>
              </OverlayTrigger>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <Badge bg="dark" className="px-3 py-2" style={{ fontSize: "0.75rem" }}>
                {rimessiRaggruppati.length} DOCUMENTI TOTALI
              </Badge>
              <Button
                variant="outline-dark"
                className="d-flex align-items-center gap-1 border-0"
                onClick={() => setShowFullscreenModal(false)}
                style={{ borderRadius: "10px", height: "38px" }}
              >
                <FiX size={18} /> Chiudi
              </Button>
            </div>
          </div>

          {/* Griglia Tabellare Completa */}
          <div className="flex-grow-1 p-3" style={{ overflowY: "auto" }}>
            <div className="bg-white shadow-sm rounded-4 overflow-hidden border">
              <Table
                responsive
                hover
                className="mb-0 align-middle text-nowrap"
                style={{ fontSize: "0.78rem" }}
              >
                <thead className="table-light border-bottom text-uppercase" style={{ fontSize: "0.70rem" }}>
                  <tr>
                    <th onClick={() => requestSort("Dep")} className="py-3 px-2">Dep</th>
                    <th onClick={() => requestSort("ID Fornitore")} className="py-3 px-2">ID Forn.</th>
                    <th onClick={() => requestSort("Ragione Sociale")} className="py-3 px-2">
                      Fornitore <MdOutlineSort />
                    </th>
                    <th onClick={() => requestSort("Num Rimesso")} className="py-3 px-2">Num. Rimesso</th>
                    <th onClick={() => requestSort("Data Rimesso")} className="py-3 px-2">Data Rimesso</th>
                    <th onClick={() => requestSort("totQta")} className="py-3 px-2 text-center">N° Righe</th>
                    <th onClick={() => requestSort("totQta")} className="py-3 px-2 text-center">Tot. Qta</th>
                    <th onClick={() => requestSort("totSpuntata")} className="py-3 px-2 text-center">Qta Spuntata</th>
                    <th onClick={() => requestSort("totImporto")} className="py-3 px-2 text-end">Importo Tot.</th>
                    <th onClick={() => requestSort("Data Rimesso")} className="py-3 px-2 text-center">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="16" className="text-center py-4 text-muted">
                        Caricamento in corso...
                      </td>
                    </tr>
                  ) : rimessiRaggruppati.length === 0 ? (
                    <tr>
                      <td colSpan="16" className="text-center py-4 text-muted">
                        Nessun rimesso trovato
                      </td>
                    </tr>
                  ) : (
                    rimessiRaggruppati.map((row, idx) => {
                      const urgente = isOlderThan5Days(row["Data Rimesso"]);
                      return (
                        <tr
                          key={idx}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedRimesso(row);
                            setShowModal(true);
                          }}
                        >
                          <td className="fw-bold">{row["Dep"]}</td>
                          <td className="text-muted">{row["ID Fornitore"] || "---"}</td>
                          <td className="fw-semibold text-dark text-truncate" style={{ maxWidth: "200px" }} title={row["Ragione Sociale"]}>
                            {row["Ragione Sociale"]}
                          </td>
                          <td className="fw-bold text-primary">{row["Num Rimesso"]}</td>
                          <td>
                            {row["Data Rimesso"]
                              ? new Date(row["Data Rimesso"]).toLocaleDateString("it-IT")
                              : "---"}
                          </td>
                          
                          <td className="text-center fw-semibold text-secondary">{row["Flag FB"] || "---"}</td>
                          <td className="text-center text-muted">{row["Codice Cassa"] || "---"}</td>
                          <td className="text-center">{row["IN Lavorazione"] || "No"}</td>
                          <td className="text-center">
                            <Badge bg={row["Cassa Chiusa"] === "S" ? "success" : "secondary"}>
                              {row["Cassa Chiusa"] || "N"}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg={row["Rimesso Chiuso"] === "S" ? "success" : "warning"} text={row["Rimesso Chiuso"] === "S" ? "white" : "dark"}>
                              {row["Rimesso Chiuso"] || "N"}
                            </Badge>
                          </td>
                          <td className="text-center fw-semibold">{row.dettaglioArticoli.length}</td>
                          <td className="text-center fw-bold text-primary">{Math.round(row.totQta)}</td>
                          <td className="text-center fw-semibold text-muted">{Math.round(row.totSpuntata)}</td>
                          <td className="text-end fw-bold text-dark">
                            € {row.totImporto ? row.totImporto.toFixed(2) : "0.00"}
                          </td>
                          <td className="text-center">
                            {urgente ? (
                              <Badge bg="danger-subtle" className="text-danger border border-danger-subtle px-2 py-1" style={{ fontSize: "0.65rem" }}>
                                <FiAlertCircle className="me-1" /> URGENTE
                              </Badge>
                            ) : (
                              <Badge bg="success-subtle" className="text-success border border-success-subtle px-2 py-1" style={{ fontSize: "0.65rem" }}>
                                <FiCheckCircle className="me-1" /> RECENTE
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO ARTICOLI (SUB-LIVELLO) */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        scrollable
        size="lg"
        dialogClassName="custom-modal-center"
      >
        <Modal.Header closeButton className="bg-light py-2 px-3 align-items-center">
          <Modal.Title style={{ fontSize: "0.9rem" }} className="fw-bold text-dark d-flex align-items-center gap-2">
            <FiFileText className="text-primary" />
            DETTAGLIO RIMESSO: <span className="text-primary">#{selectedRimesso?.["Num Rimesso"]}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3 bg-light">
          {/* SEC. 1: INFORMAZIONI PRINCIPALI */}
          <Card className="border-0 shadow-sm rounded-3 mb-3">
            <Card.Body className="p-3">
              <Row className="g-3 align-items-center">
                <Col md={6}>
                  <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: "0.65rem" }}>
                    Fornitore
                  </small>
                  <div className="fw-bold fs-6 text-dark text-truncate" title={selectedRimesso?.["Ragione Sociale"]}>
                    {selectedRimesso?.["Ragione Sociale"] || "---"}
                  </div>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                    ID Fornitore: <span className="fw-semibold text-dark">{selectedRimesso?.["ID Fornitore"] || "N/D"}</span>
                  </small>
                </Col>

                <Col md={3}>
                  <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: "0.65rem" }}>
                    Data Documento
                  </small>
                  <span className="fw-bold text-dark" style={{ fontSize: "0.85rem" }}>
                    {selectedRimesso?.["Data Rimesso"]
                      ? new Date(selectedRimesso["Data Rimesso"]).toLocaleDateString("it-IT", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : "---"}
                  </span>
                  <small className="d-block text-muted" style={{ fontSize: "0.7rem" }}>
                    <FiClock className="me-1" />
                    {calcolaGiorniFeriali(selectedRimesso?.["Data Rimesso"])} giorni lavorativi
                  </small>
                </Col>

              
              </Row>
            </Card.Body>
          </Card>

          {/* SEC. 2: METADATI E STATI DEL DOCUMENTO (JSON METRICS) */}
          

          {/* SEC. 3: RIEPILOGO TOTALI */}
          <Row className="g-2 mb-3">
            <Col sm={4}>
              <div className="bg-white p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted d-block" style={{ fontSize: "0.65rem" }}>TOTALE PEZZI</small>
                  <span className="fw-bold text-primary fs-6">{selectedRimesso?.totQta || 0}</span>
                </div>
                <FiBox className="text-primary opacity-50" size={20} />
              </div>
            </Col>
            <Col sm={4}>
              <div className="bg-white p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted d-block" style={{ fontSize: "0.65rem" }}>QTA SPUNTATA</small>
                  <span className="fw-bold text-success fs-6">{selectedRimesso?.totSpuntata || 0}</span>
                </div>
                <FiCheckCircle className="text-success opacity-50" size={20} />
              </div>
            </Col>
            <Col sm={4}>
              <div className="bg-white p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted d-block" style={{ fontSize: "0.65rem" }}>IMPORTO TOTALE</small>
                  <span className="fw-bold text-dark fs-6">
                    € {selectedRimesso?.totImporto ? selectedRimesso.totImporto.toFixed(2) : "0.00"}
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          {/* SEC. 4: TABELLA DETTAGLIO RIGHE ARTICOLO */}
          <div className="bg-white rounded-3 border overflow-hidden shadow-sm">
            <div className="p-2 px-3 bg-white border-bottom d-flex justify-content-between align-items-center">
              <span className="fw-bold text-secondary" style={{ fontSize: "0.75rem" }}>
                ARTICOLI CONTENUTI ({selectedRimesso?.dettaglioArticoli.length || 0})
              </span>
            </div>
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                <tr>
                  <th className="ps-3 py-2 text-muted">Codice Articolo</th>
                  <th className="text-center py-2 text-muted" style={{ width: "90px" }}>Qta Doc.</th>
                  <th className="text-center py-2 text-muted" style={{ width: "100px" }}>Qta Spuntata</th>
                  <th className="text-end pe-3 py-2 text-muted" style={{ width: "100px" }}>Importo</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "0.75rem" }}>
                {selectedRimesso?.dettaglioArticoli.map((art, i) => (
                  <tr key={i}>
                    <td className="ps-3 py-2 fw-semibold text-dark">
                      {art.articolo || "---"}
                    </td>
                    <td className="text-center py-2 fw-bold text-primary">
                      {Math.round(art.Qta || 0)}
                    </td>
                    <td className="text-center py-2 fw-semibold text-muted">
                      {Math.round(art["Qta Spuntata"] || 0)}
                    </td>
                    <td className="text-end pe-3 py-2 fw-semibold text-dark">
                      € {parseFloat(art.Importo || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer className="p-2 bg-light border-top">
          <Button
            variant="secondary"
            size="sm"
            className="px-3"
            style={{ fontSize: "0.75rem" }}
            onClick={() => setShowModal(false)}
          >
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

</div>
  );
};

export default TabellaRimessiCompleta;